import React, { useCallback, useEffect, useState } from "react";
import FlexibleTable from "../../../modules/FlexibleTable";
import FlexibleTableLabelWithImage from "../../../modules/FlexibleTableLabelWithImage";
import { SyncStatus } from "../SyncConfig/SyncStatus";
import { humanizeDiffUnixAndNow } from "../../../utils/DateTimeUtil";
import ToggleSwitch from "../../../modules/ToggleSwitch";
import clsx from "clsx";
import EmptyColumnPlaceholder from "../../../modules/EmptyColumnPlaceholder";
import { Link } from "react-router";
import Separator from "../../../modules/Separator";
import TableCentricPageButton from "../../../modules/TableCentricPageButton";
import TableLeftPanel from "../../../utils/TableLeftPanel";
import TableCentricPage from "../../../modules/TableCentricPage";
import SleekModal from "../../../modules/SleekModal";
import SisProviderIconButton from "./SisProviderIconButton";
import SisDeleteDialog from "./SisDeleteDialog";
import { SisWondeForm } from "./SisWondeForm";
import { SisOneRosterForm } from "./SisOneRosterForm";
import { SisSchoolExcludeForm } from "./SisExcludeSchoolForm";
import { SyncExecution } from "../interfaces/GetSyncExecutions";
import { SisPowerSchoolForm } from "./SisPowerSchoolForm";
import { InlineNotification, useToast, useTranslation } from "@familyzone/component-library";
import { Box } from "@chakra-ui/react";
import { SyncMetricKeyEnum } from "../../enums/SysncMetricKeyEnum";
import { PurgeConfirmModal } from "../SyncConfig/ConfigForm/PurgeConfirmModal";
import SessionStore from "../../../stores/SessionStore";
import { purgeSyncData } from "../../../utils/api/Purge";
import { useSessionStore } from "../../../storez/SessionStore";
import {
  deleteSyncConfiguration,
  executeSync,
  extractSyncError,
  listSyncConfigurations,
  setSyncEnabled,
  SIS_SYNC_TYPES,
} from "../../../utils/api/SyncApi";
import { mapSyncNodeToSISProvider, saveSisSync } from "./mapSyncNodeToSISProvider";

interface RunningSyncsMap {
  [key: string]: number;
}

export interface ProviderData {
  id?: string;
  type: string;
  enabled: boolean;
  integration_type: string;
  name: string;
  sync_parents?: boolean;
  sync_parent_types?: string[];
  sync_classrooms?: boolean;
  respect_enrolment_date?: boolean;
  respect_sessions?: boolean;
  excluded_schools_list?: string[];
  api_configuration: APIConfiguration;
  merge_config?: MergeConfig;
  sync_attributes?: AttributesConfig;
  compatibility_mode?: boolean;
}

export interface AttributesConfig {
  years?: AttributeMapping;
}

export interface AttributeMapping {
  enabled: boolean;
  mappings: Record<string, YearAttributeEnum>;
}

export enum YearAttributeEnum {
  IT = "IT",
  PR = "PR",
  PK = "PK",
  TK = "TK",
  KG = "KG",
  YEAR_01 = "YEAR_01",
  YEAR_02 = "YEAR_02",
  YEAR_03 = "YEAR_03",
  YEAR_04 = "YEAR_04",
  YEAR_05 = "YEAR_05",
  YEAR_06 = "YEAR_06",
  YEAR_07 = "YEAR_07",
  YEAR_08 = "YEAR_08",
  YEAR_09 = "YEAR_09",
  YEAR_10 = "YEAR_10",
  YEAR_11 = "YEAR_11",
  YEAR_12 = "YEAR_12",
  YEAR_13 = "YEAR_13",
  PS = "PS",
  UG = "UG",
  OTHER = "OTHER",
}

export interface MergeConfig {
  priority: number;
  attachOnly?: boolean;
  email?: {
    stripped: boolean;
  };
  computed?: {
    expression: string;
  };
}

export interface APIConfiguration {
  client_id?: string;
  client_secret?: string;
  endpoint_url?: string;
  token_url?: string;
  access_token?: string;
  classname_keys?: string;
  district_id?: string;
  domain?: string;
  email_key?: string;
  user_email_key?: string;
  guardian_email_key?: string;
  guardian_roles?: string[];
  user_match?: string;
  username_key?: string;
  teacher_username_key?: string;
}

export interface SISProvider {
  api_configuration: APIConfiguration;
  enabled: boolean;
  id: string;
  integration_type: string;
  name: string;
  respect_enrolment_date: boolean;
  respect_sessions: boolean;
  sync_parents: boolean;
  sync_classrooms: boolean;
  type: SisVariant;
  status?: Status;
  excluded_schools_list?: string[];
  sync_executions?: SyncExecution[];
  sync_parent_types?: string[];
  merge_config?: MergeConfig;
  sync_attributes?: AttributesConfig;
  compatibility_mode?: boolean;
  read_error?: string;
}

export interface Status {
  end_time?: number;
  error?: string;
  start_time?: number;
  sync_id?: string;
  data_totality?: DataTotality;
  partial_errors?: string[][];
}

export enum DataTotality {
  Full = "Full",
  Partial = "Partial",
}

export interface SISProvidersResponse {
  sis_providers: SISProvider[];
}

export enum SisVariant {
  Oneroster = "oneroster",
  Classlink = "classlink",
  Powerschool = "powerschool",
  Wonde = "wonde",
}

export const SisIntegration: React.FC = () => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [showAddNew, setShowAddNew] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<SisVariant | null>();
  const [showExcludeSchoolForm, setShowExcludeSchoolForm] = useState<boolean>(false);
  const [rowToEdit, setRowToEdit] = useState<SISProvider | null>(null);
  const [rowToViewLog, setRowToViewLog] = useState<SISProvider | null>(null);
  const [rowToDelete, setRowToDelete] = useState<SISProvider | null>(null);
  const [rowToPurge, setRowToPurge] = useState<SISProvider | null>(null);
  const [isPurging, setIsPurging] = useState<boolean>(false);
  const [multipleSyncsConfigured, setMultipleSyncsConfigured] = useState<boolean>(false);
  const [data, setData] = useState<SISProvider[]>([]);
  const { successToast, errorToast } = useToast();
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
  const device: string = SessionStore.getSelectedDevice() as string;
  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();

  const fetchData = useCallback(() => {
    setLoaded(false);
    let data: SISProvider[] = [];

    // Each provider's `status` is already derived from the list response's `sync`
    // summary (see mapSyncNodeToSISProvider), so no per-row executions call is needed.
    const providersSource: Promise<SISProvider[]> = listSyncConfigurations(SIS_SYNC_TYPES).then((conn) =>
      conn.edges.map((edge) => mapSyncNodeToSISProvider(edge.node))
    );

    void providersSource
      .then((providers) => {
        data = providers;

        let activeSyncCount = 0;
        data.forEach((sis) => {
          if (sis.enabled) {
            activeSyncCount++;
          }
        });
        setData(data);
        setMultipleSyncsConfigured(activeSyncCount > 1);
        setLoaded(true);
      })
      .catch(() => {
        setData([]);
        setMultipleSyncsConfigured(false);
        setLoaded(true);
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isSyncInProgress = useCallback((row: SISProvider): boolean => {
    if (!row.status || row.status.error) return false;
    return !!(row.status.start_time && !row.status.end_time);
  }, []);

  const isSyncStartable = useCallback((row: SISProvider): boolean => {
    const now = new Date();
    const manuallySetRunningSyncs = JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}") as RunningSyncsMap;
    const manualStatusExpiry = manuallySetRunningSyncs[row.id];
    if (manualStatusExpiry > now.getTime()) return true;

    if (!row.status || row.status.error) return false;

    // Check if start_time is over 15 minutes ago
    const fifteenMinutesAgo = now.getTime() - 15 * 60 * 1000; // 15 minutes in milliseconds
    const startTime = row.status.start_time ? new Date(row.status.start_time).getTime() : null;
    // startTime only has seconds, so * 1000.
    if (startTime && startTime * 1000 < fifteenMinutesAgo) {
      return false;
    }

    return !!(row.status.start_time && !row.status.end_time);
  }, []);

  // `isSyncInProgress` and `isSyncStartable` each catch a case the other misses:
  // a run started over 15 minutes ago is in progress but no longer inside the
  // lockout, and a run triggered seconds ago is inside the lockout before any
  // status exists to make it "in progress". Destructive actions have to be
  // refused across both, so they share one predicate with the Sync link.
  const operationsBlocked = useCallback(
    (row: SISProvider): boolean => isSyncInProgress(row) || isSyncStartable(row),
    [isSyncInProgress, isSyncStartable]
  );

  const fetchDataWithSyncDelay = useCallback(() => {
    setLoaded(false);
    setTimeout(() => {
      fetchData();
    }, 1500);
  }, [fetchData]);

  const updateProvider = useCallback(
    (id: string, row: ProviderData) => {
      setLoaded(false);
      // SM-API: PATCH /api/syncs/{id} with the mapped v2 input.
      void saveSisSync(row, id)
        .then(fetchData)
        .catch((e) => {
          errorToast({
            title: t("Failed to update sync"),
            description: extractSyncError(e, t("The change could not be saved. Please try again.")),
          });
          fetchData(); // resync UI with server truth
        });
    },
    [fetchData, errorToast, t]
  );

  const handle_viewSyncLog = useCallback((row: SISProvider) => {
    setRowToViewLog(row);
  }, []);

  const handle_updateExcludeSchools = useCallback(
    (schoolIDsToExclude: string[], rowEdited: SISProvider) => {
      const apiData: ProviderData = {
        ...rowEdited,
        excluded_schools_list: schoolIDsToExclude,
      };
      updateProvider(rowEdited.id, apiData);
      setShowExcludeSchoolForm(false);
      setRowToEdit(null);
    },
    [updateProvider]
  );

  const handle_wondeFormSubmit = useCallback(() => {
    fetchData();
    setShowForm(null);
    setRowToEdit(null);
  }, [fetchData]);

  const handle_oneRosterFormSubmit = useCallback(() => {
    fetchData();
    setShowForm(null);
    setRowToEdit(null);
  }, [fetchData]);

  const handle_powerschoolFormSubmit = useCallback(() => {
    fetchData();
    setShowForm(null);
    setRowToEdit(null);
  }, [fetchData]);

  const handle_delete_clicked = useCallback(
    (row: SISProvider) => {
      if (operationsBlocked(row)) {
        return;
      }

      setRowToDelete(row);
    },
    [operationsBlocked]
  );

  const handle_delete_confirmation = useCallback(() => {
    if (!rowToDelete) return;
    if (operationsBlocked(rowToDelete)) {
      return;
    }

    const id = rowToDelete.id;
    // SM-API: DELETE /api/syncs/{id}
    void deleteSyncConfiguration(id)
      .then(fetchData)
      .catch(() => errorToast({ title: t("Failed to delete sync"), description: t("The sync could not be deleted. Please try again.") }));
    setRowToDelete(null);
  }, [operationsBlocked, fetchData, rowToDelete, errorToast, t]);

  const handle_purge_clicked = useCallback(
    (row: SISProvider) => {
      if (operationsBlocked(row)) {
        return;
      }

      setRowToPurge(row);
    },
    [operationsBlocked]
  );

  const handle_purge_confirmation = useCallback(async () => {
    if (!rowToPurge) return;

    setIsPurging(true);
    try {
      await purgeSyncData(rowToPurge.id, device);
      setRowToPurge(null);
      fetchData();
      successToast({
        title: t("Purge triggered successfully"),
      });
    } catch (error) {
      errorToast({
        title: t("Failed to trigger purge"),
        description: t(error instanceof Error ? error.message : "An unknown error occurred while trying to purge data."),
      });
    } finally {
      setIsPurging(false);
    }
  }, [rowToPurge, device, fetchData, successToast, errorToast, t]);

  const handle_edit = useCallback(
    (row: SISProvider) => {
      if (isSyncStartable(row)) {
        return;
      }

      switch (row.type) {
        case "wonde":
          setShowForm(SisVariant.Wonde);
          setRowToEdit(row);
          break;
        case "oneroster":
          setShowForm(SisVariant.Oneroster);
          setRowToEdit(row);
          break;
        case "classlink":
          setShowForm(SisVariant.Classlink);
          setRowToEdit(row);
          break;
        case "powerschool":
          setShowForm(SisVariant.Powerschool);
          setRowToEdit(row);
          break;
        default:
          break;
      }
    },
    [isSyncStartable]
  );

  const handle_include_exclude_schools = useCallback(
    (row: SISProvider) => {
      if (isSyncStartable(row)) {
        return;
      }

      switch (row.type) {
        case "wonde":
          break;
        case "oneroster":
          setRowToEdit(row);
          setShowExcludeSchoolForm(true);
          break;
        case "classlink":
          setShowExcludeSchoolForm(true);
          setRowToEdit(row);
          break;
        case "powerschool":
          setShowExcludeSchoolForm(true);
          setRowToEdit(row);
          break;
        default:
          break;
      }
    },
    [isSyncStartable]
  );

  const handle_sync = useCallback(
    (row: SISProvider) => {
      if (!row.enabled || isSyncStartable(row)) {
        return;
      }

      const handleSuccess = () => {
        const manuallySetRunningSyncs = JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}") as RunningSyncsMap;
        const now = new Date();
        manuallySetRunningSyncs[row.id] = now.getTime() + 60000; // 60 second 'ttl' to lock out this sync in this browser
        localStorage.setItem("manuallySetRunningSyncs", JSON.stringify(manuallySetRunningSyncs));
        fetchDataWithSyncDelay();
      };

      // SM-API: POST /api/syncs/{id}/execute (same endpoint for every provider).
      executeSync(row.id)
        .then(handleSuccess)
        .catch((e) =>
          errorToast({
            title: t("Failed to start sync"),
            description: extractSyncError(e, t("The sync could not be started. Please try again.")),
          })
        );
    },
    [isSyncStartable, fetchDataWithSyncDelay, errorToast, t]
  );

  const handle_enabledToggleChange = useCallback(
    (row: SISProvider) => {
      if (isSyncInProgress(row)) {
        return;
      }

      const newEnabled = !row.enabled;

      setData((prev) => prev.map((item) => (item.id === row.id ? { ...item, enabled: newEnabled } : item)));
      setLoaded(false);
      void setSyncEnabled(row.id, newEnabled)
        .then(fetchData)
        .catch((e) => {
          errorToast({
            title: t("Failed to update sync"),
            description: extractSyncError(e, t("The change could not be saved. Please try again.")),
          });
          fetchData(); // resync UI with server truth
        });
    },
    [isSyncInProgress, fetchData, errorToast, t]
  );

  const getIconByProvider = (variant: SisVariant): string | null => {
    switch (variant) {
      case SisVariant.Wonde:
        return "/static/images/icon_wonde.svg";
      case SisVariant.Oneroster:
        return "/static/images/icon_oneroster.svg";
      case SisVariant.Classlink:
        return "/static/images/icon_classlink.svg";
      case SisVariant.Powerschool:
        return "/static/images/icon_powerschool.png";
      default:
        return null;
    }
  };

  const renderViewLog = () => {
    if (!rowToViewLog) return null;

    return (
      <SyncStatus
        syncType={rowToViewLog.type === "wonde" ? "WONDE" : "ONEROSTER"}
        syncId={rowToViewLog.id}
        hideAndIgnoreSyncState={true}
        hideSyncButton={true}
        metricsToHide={[SyncMetricKeyEnum.userClassroomsDeleted, SyncMetricKeyEnum.userGuardiansDeleted]}
      />
    );
  };

  const renderHeaderButtons = () => (
    <>
      <TableCentricPageButton
        title="Add New"
        onClickHandler={() => {
          setRowToEdit(null);
          setShowAddNew(true);
        }}
      />
      <TableCentricPageButton title="Refresh" onClickHandler={() => fetchData()} />
    </>
  );

  const emptyText = "---";
  const neverSyncedText = "Never synced";

  const syncDetailsText = useCallback(
    (row: SISProvider): string => {
      if (!row.status || isSyncInProgress(row) || !row.status.start_time) {
        return emptyText;
      }

      if (row.status.data_totality === DataTotality.Partial) {
        return "Partial sync";
      }

      return row.status.error ? "Error" : "Sync successful";
    },
    [isSyncInProgress]
  );

  const sortableSync = useCallback(
    (rowA: SISProvider, rowB: SISProvider): number => {
      //Never synced
      const statusA = rowA.status;
      const statusB = rowB.status;
      if (!statusA && !statusB) {
        return 0;
      } else if (!statusA) {
        return -1;
      } else if (!statusB) {
        return 1;
      }

      //In progress
      const inProgressA = isSyncInProgress(rowA);
      const inProgressB = isSyncInProgress(rowB);
      if (inProgressA && inProgressB) {
        return 0;
      } else if (inProgressA) {
        return 1;
      } else if (inProgressB) {
        return -1;
      }

      const timeA = rowA.status?.error ? rowA.status.start_time : rowA.status?.end_time;
      const timeB = rowB.status?.error ? rowB.status.start_time : rowB.status?.end_time;
      if (!timeA || !timeB) return 0;
      return timeA === timeB ? 0 : timeA < timeB ? -1 : 1;
    },
    [isSyncInProgress]
  );

  const sortableDetails = useCallback(
    (rowA: SISProvider, rowB: SISProvider): number => {
      return FlexibleTable.sort_caseinsensitive_strings(syncDetailsText(rowA), syncDetailsText(rowB));
    },
    [syncDetailsText]
  );

  const lastSyncText = useCallback(
    (row: SISProvider): string => {
      if (!row.status) {
        return neverSyncedText;
      }
      if (row.status.error) {
        return humanizeDiffUnixAndNow(row.status.start_time, "Last Sync Unavailable");
      }
      if (isSyncInProgress(row)) {
        return "In progress";
      }
      return humanizeDiffUnixAndNow(row.status.end_time, "Last Sync Unavailable");
    },
    [isSyncInProgress]
  );

  const syncStatusText = useCallback((row: SISProvider): string => {
    if (!row.status) {
      return emptyText;
    }
    if (row.status.error) {
      return "Failed";
    }
    if (row.status.start_time && row.status.end_time) {
      return "Synced";
    }
    return emptyText;
  }, []);

  const renderContent = useCallback(() => {
    const columns = [
      {
        title: "System",
        data: (row: SISProvider) => <FlexibleTableLabelWithImage label={row.name} imgUrl={getIconByProvider(row.type)} />,
        sortable: FlexibleTable.sortable__single_caseinsensitive_string_field("name"),
        search: FlexibleTable.search__single_string_field("name"),
      },
      {
        title: "Enabled",
        data: (row: SISProvider) => <ToggleSwitch enabled={row.enabled} onChange={() => handle_enabledToggleChange(row)} />,
      },
      {
        title: "Last Sync",
        data: (row: SISProvider) => {
          const syncText = lastSyncText(row);
          const className = clsx("no-wrap", { "text-disabled": !row.enabled });
          if (!row.status || isSyncInProgress(row)) {
            return <span className={className}>{syncText}</span>;
          }
          return <span>{syncText}</span>;
        },
        search: FlexibleTable.search__with_function(lastSyncText),
        sortable: sortableSync,
      },
      {
        title: "Sync Status",
        data: (row: SISProvider) => {
          if (!row.status) {
            return <EmptyColumnPlaceholder />;
          }
          if (row.status.error && row.status.data_totality !== DataTotality.Partial) {
            return (
              <span className="no-wrap label-with-small-icon-left error text-uppercase">
                <i className="fa fa-exclamation-circle" />
                Failed
              </span>
            );
          }
          if (row.status.start_time && row.status.end_time) {
            const successClassName = clsx({ "text-disabled": !row.enabled });
            return <span className={successClassName}>Synced</span>;
          }

          return <EmptyColumnPlaceholder />;
        },
        sortable: sortableDetails,
        search: FlexibleTable.search__with_function(syncStatusText),
      },
      {
        title: "Sync Details",
        data: (row: SISProvider) => {
          if (!row.status || isSyncInProgress(row) || !row.status.start_time) {
            return <EmptyColumnPlaceholder />;
          }

          const textClassName = clsx(
            (row.status.error || row.status.data_totality === DataTotality.Partial) && "error",
            !row.enabled && "text-disabled"
          );
          return (
            <span className="sis-sync-details">
              <span className={textClassName}>{syncDetailsText(row)}</span>
              <Link onClick={() => handle_viewSyncLog(row)} to={""}>
                [view log]
              </Link>
            </span>
          );
        },
        sortable: sortableDetails,
        search: FlexibleTable.search__with_function(syncDetailsText),
      },
      {
        title: "Operations",
        data: (row: SISProvider) => {
          const rowOperationsClassName = clsx("row-operations", isSyncStartable(row) && "row-operations-disabled");
          const syncActionClassName = clsx(!row.enabled && "disabled");
          // Delete and Purge are blocked for as long as Sync is, so they have to grey
          // out on the same condition. `destructive` is dropped rather than added to,
          // because `.row-operations a.destructive` outranks the whole-row greying and
          // would keep them looking live while every sibling link greys out.
          //
          // Deliberately not tied to `row.enabled`: a disabled sync is still a
          // configuration an admin may legitimately delete or purge.
          const destructiveActionClassName = clsx(operationsBlocked(row) ? "disabled" : "destructive");
          return (
            <span className={rowOperationsClassName}>
              <Link to={""} className={syncActionClassName} onClick={() => handle_sync(row)}>
                Sync
              </Link>
              <Separator />
              <Link to={""} onClick={() => handle_edit(row)}>
                Edit
              </Link>
              {row.type !== SisVariant.Wonde && (
                <>
                  <Separator />
                  <Link to={""} onClick={() => handle_include_exclude_schools(row)}>
                    Exclude Schools
                  </Link>
                </>
              )}
              <Separator />
              <Link to={""} className={destructiveActionClassName} onClick={() => handle_delete_clicked(row)}>
                Delete
              </Link>
              {isSupportAdmin && row.id && (
                <>
                  <Separator />
                  <Link to={""} className={destructiveActionClassName} onClick={() => handle_purge_clicked(row)}>
                    Purge data
                  </Link>
                </>
              )}
            </span>
          );
        },
      },
    ];

    return (
      <>
        {multipleSyncsConfigured && (
          <Box sx={{ marginBottom: "0.5rem" }}>
            <InlineNotification
              status="warning"
              notificationTitle={"Important"}
              notificationDescription={
                "Multiple SIS Integrations are configured. If multiple syncs are required, ensure they do not duplicate data such as Students, Guardians, Schools or Classrooms."
              }
            />
          </Box>
        )}
        <FlexibleTable columns={columns} data={data} movable={false} loaded={loaded} />
      </>
    );
  }, [
    multipleSyncsConfigured,
    handle_include_exclude_schools,
    lastSyncText,
    sortableSync,
    sortableDetails,
    syncStatusText,
    operationsBlocked,
    syncDetailsText,
    data,
    loaded,
    handle_enabledToggleChange,
    isSyncInProgress,
    isSyncStartable,
    handle_viewSyncLog,
    handle_sync,
    handle_edit,
    handle_delete_clicked,
    handle_purge_clicked,
    isSupportAdmin,
  ]);

  return (
    <React.Fragment>
      <TableLeftPanel>
        <TableCentricPage title="School Information Systems" buttons={renderHeaderButtons()} content={renderContent()} />
      </TableLeftPanel>
      <SleekModal
        show={showAddNew}
        title="Add new SIS integration"
        description="Which SIS integration would you like to add?"
        handleHide={() => setShowAddNew(false)}
      >
        <div className="sis-add-new-provider-icons-container">
          <SisProviderIconButton
            label="OneRoster"
            imgSrc={getIconByProvider(SisVariant.Oneroster)}
            onClick={() => {
              setShowForm(SisVariant.Oneroster);
              setShowAddNew(false);
            }}
          />
          <SisProviderIconButton
            label="Wonde"
            imgSrc={getIconByProvider(SisVariant.Wonde)}
            onClick={() => {
              setShowForm(SisVariant.Wonde);
              setShowAddNew(false);
            }}
          />
          <SisProviderIconButton
            label="ClassLink"
            imgSrc={getIconByProvider(SisVariant.Classlink)}
            onClick={() => {
              setShowForm(SisVariant.Classlink);
              setShowAddNew(false);
            }}
          />
          <SisProviderIconButton
            label="PowerSchool"
            imgSrc={getIconByProvider(SisVariant.Powerschool)}
            onClick={() => {
              setShowForm(SisVariant.Powerschool);
              setShowAddNew(false);
            }}
          />
        </div>
      </SleekModal>
      <SleekModal show={!!rowToViewLog} handleHide={() => setRowToViewLog(null)}>
        {rowToViewLog && renderViewLog()}
      </SleekModal>
      <SisDeleteDialog
        modalClassName="sis-delete-modal"
        show={rowToDelete}
        handleHide={() => setRowToDelete(null)}
        handleDeleteConfirmed={handle_delete_confirmation}
      />
      <PurgeConfirmModal
        isOpen={!!rowToPurge}
        onClose={() => setRowToPurge(null)}
        onConfirm={() => void handle_purge_confirmation()}
        isLoading={isPurging}
      />
      {/* we do this to force the unmounting and remounting */}
      {(showForm === SisVariant.Oneroster || showForm === SisVariant.Classlink) && (
        <SisOneRosterForm
          show
          isClasslink={showForm === SisVariant.Classlink}
          rowToEdit={rowToEdit}
          handleHide={() => {
            setRowToEdit(null);
            setShowForm(null);
          }}
          handleSubmit={handle_oneRosterFormSubmit}
          multipleSyncsConfigured={multipleSyncsConfigured}
        />
      )}

      {showForm === SisVariant.Powerschool && (
        <SisPowerSchoolForm
          show
          rowToEdit={rowToEdit}
          handleHide={() => {
            setRowToEdit(null);
            setShowForm(null);
          }}
          handleSubmit={handle_powerschoolFormSubmit}
          multipleSyncsConfigured={multipleSyncsConfigured}
        />
      )}
      {showForm === SisVariant.Wonde && (
        <SisWondeForm
          show
          rowToEdit={rowToEdit}
          handleHide={() => {
            setRowToEdit(null);
            setShowForm(null);
          }}
          handleSubmit={handle_wondeFormSubmit}
          multipleSyncsConfigured={multipleSyncsConfigured}
        />
      )}
      {showExcludeSchoolForm && rowToEdit && (
        <SisSchoolExcludeForm
          show={showExcludeSchoolForm}
          rowToEdit={rowToEdit}
          handleHide={() => {
            setRowToEdit(null);
            setShowExcludeSchoolForm(false);
          }}
          handleSubmit={handle_updateExcludeSchools}
        />
      )}
    </React.Fragment>
  );
};
